import { Badge, Box } from '@mui/material';
import { NavLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { colors } from '../core/theme/theme';

export const BOTTOM_TAB_BAR_HEIGHT = 64;

interface Props {
  onOpenSearch: () => void;
  onOpenMore: () => void;
  unreadCount: number;
}

export default function BottomTabBar({ onOpenSearch, onOpenMore, unreadCount }: Props) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const isActive = (path: string) =>
    pathname === path || (path !== '/' && pathname.startsWith(path));

  const slotSx = (active: boolean) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    minHeight: 44,
    textDecoration: 'none',
    color: active ? colors.bodyText : colors.textMuted,
    fontWeight: active ? 700 : 500,
    fontSize: 10,
    fontFamily: 'inherit',
    borderTop: active ? `3px solid ${colors.yellow}` : '3px solid transparent',
    paddingTop: active ? '1px' : '4px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  });

  return (
    <Box
      component="nav"
      aria-label={t('nav.primaryNavigation')}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: BOTTOM_TAB_BAR_HEIGHT,
        paddingBottom: 'env(safe-area-inset-bottom)',
        bgcolor: '#FFFFFF',
        borderTop: `1px solid ${colors.lightGray}`,
        display: 'flex',
        alignItems: 'stretch',
        zIndex: 1200,
      }}
    >
      <NavLink
        to="/projects"
        style={slotSx(isActive('/projects'))}
        data-active={isActive('/projects')}
        aria-label={t('nav.projectsHub')}
      >
        <FolderOutlinedIcon sx={{ fontSize: 22 }} />
        <span>{t('nav.projectsHub')}</span>
      </NavLink>

      <NavLink
        to="/kronos/tracker"
        style={slotSx(isActive('/kronos'))}
        data-active={isActive('/kronos')}
        aria-label={t('nav.tracker')}
      >
        <TimerOutlinedIcon sx={{ fontSize: 22 }} />
        <span>{t('nav.tracker')}</span>
      </NavLink>

      {/* Raised Search button */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        <button
          type="button"
          onClick={onOpenSearch}
          aria-label={t('nav.search')}
          style={{
            position: 'absolute',
            top: -16,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 56,
            height: 56,
            background: colors.yellow,
            color: '#000000',
            border: '3px solid #FFFFFF',
            borderRadius: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <SearchIcon sx={{ fontSize: 26 }} />
        </button>
      </Box>

      <NavLink
        to="/notifications"
        style={slotSx(isActive('/notifications'))}
        data-active={isActive('/notifications')}
        aria-label={t('nav.notifications')}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          overlap="circular"
          sx={{ '& .MuiBadge-badge': { borderRadius: '6px', fontWeight: 700 } }}
        >
          <NotificationsOutlinedIcon sx={{ fontSize: 22 }} />
        </Badge>
        <span>{t('nav.notifications')}</span>
      </NavLink>

      <button
        type="button"
        onClick={onOpenMore}
        aria-label={t('nav.more')}
        style={slotSx(false)}
      >
        <MoreHorizIcon sx={{ fontSize: 22 }} />
        <span>{t('nav.more')}</span>
      </button>
    </Box>
  );
}
